import { Button, ButtonProps, cn } from '@heroui/react';
import { Copy, Check } from 'iconoir-react';

import { useCopyToClipboard } from '~/common/hooks/useCopyToClipboard';

interface CopyableTextProps {
  text: string;
  children: React.ReactNode;
  iconSize?: 'icon-smallest' | 'icon-small' | 'icon-medium' | 'icon-large';
  className?: ButtonProps['className'];
  'data-testid'?: string;
}

export const CopyableText = ({
  text,
  children,
  iconSize = 'icon-smallest',
  className,
  'data-testid': dataTestId
}: CopyableTextProps) => {
  const { copy, copiedText } = useCopyToClipboard();

  const isCopied = copiedText === text;

  const handleCopy = async () => {
    await copy(text);
  };

  return (
    <Button
      className={cn('h-fit p-0', className)}
      onPress={handleCopy}
      aria-label='Copy to clipboard'
      variant='light'
      data-testid={dataTestId}
      endContent={
        <span className='relative inline-block'>
          <span
            className={cn(
              'absolute inset-0 transition-opacity duration-200',
              isCopied ? 'opacity-100' : 'opacity-0'
            )}
          >
            <Check className={iconSize} />
          </span>
          <span
            className={cn(
              'transition-opacity duration-200',
              isCopied ? 'opacity-0' : 'opacity-0 group-hover:opacity-100'
            )}
          >
            <Copy className={iconSize} />
          </span>
        </span>
      }
    >
      <span className='transition-all duration-200 group-hover:underline group-hover:decoration-1 group-hover:underline-offset-2'>
        {children}
      </span>
    </Button>
  );
};
